import { useEffect, useState } from "react";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 200;

export interface Patient {
  id: number | string;
  fullName: string;
  patientPath: string;
  age?: number | null;
  createdOn?: string | null;
  dateOfBirth?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  consultSchedulingPath?: string | null;
  operationSchedulingPath?: string | null;
}

export interface PatientDobFilters {
  year: string;
  month: string;
  day: string;
}

interface SearchResponse {
  patients?: Patient[];
}

export function patientMeta(patient: Patient): string[] {
  return [
    patient.dateOfBirth ? `DOB ${patient.dateOfBirth}` : null,
    patient.primaryPhone || null,
    patient.primaryEmail || null,
  ].filter((value): value is string => Boolean(value));
}

export function patientSearchSummary({
  trimmedQuery,
  hasDobFilters = false,
  loading,
  errorMessage,
  resultsCount,
  idleMessage = "Search by patient name, phone, email, or DOB.",
  emptyMessage = "No matching patients found.",
}: {
  trimmedQuery: string;
  hasDobFilters?: boolean;
  loading: boolean;
  errorMessage: string;
  resultsCount: number;
  idleMessage?: string;
  emptyMessage?: string;
}): string {
  if (trimmedQuery.length < MIN_QUERY_LENGTH && !hasDobFilters) return idleMessage;
  if (loading) return "Searching patients...";
  if (errorMessage) return errorMessage;
  if (resultsCount === 0) return emptyMessage;

  return `${resultsCount} patient match${resultsCount === 1 ? "" : "es"} found.`;
}

export function usePatientSearch(searchUrl: string) {
  const [query, setQuery] = useState("");
  const [dobFilters, setDobFilters] = useState<PatientDobFilters>({ year: "", month: "", day: "" });
  const [results, setResults] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const trimmedQuery = query.trim();
  const hasDobFilters = Object.values(dobFilters).some(Boolean);

  useEffect(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH && !hasDobFilters) {
      setResults([]);
      setLoading(false);
      setErrorMessage("");
      return undefined;
    }

    setResults([]);
    setLoading(true);
    setErrorMessage("");

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const searchParams = new URLSearchParams();
        if (trimmedQuery) searchParams.set("q", trimmedQuery);
        if (dobFilters.year) searchParams.set("dob_year", dobFilters.year);
        if (dobFilters.month) searchParams.set("dob_month", dobFilters.month);
        if (dobFilters.day) searchParams.set("dob_day", dobFilters.day);

        const response = await fetch(`${searchUrl}?${searchParams.toString()}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Search failed with status ${response.status}`);
        }

        const payload: SearchResponse = await response.json();
        setResults(Array.isArray(payload.patients) ? payload.patients : []);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") return;

        setResults([]);
        setErrorMessage("Patient search is unavailable right now.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [dobFilters.day, dobFilters.month, dobFilters.year, hasDobFilters, searchUrl, trimmedQuery]);

  const clearSearch = () => {
    setQuery("");
    setDobFilters({ year: "", month: "", day: "" });
  };

  return {
    clearSearch,
    dobFilters,
    errorMessage,
    hasDobFilters,
    loading,
    query,
    results,
    setDobFilters,
    setQuery,
    trimmedQuery,
  };
}
